import { ApiError } from "../api/types";
import { handleError } from "api/client";
import { ChildrenOnly } from "common/types";
import { AsyncState, useAsync } from "@react-hookz/web";
import { createContext, useContext, useState } from "react";
import { Hall } from "../../../backend/src/halls/types";
import { getHalls } from "api/halls";
import {
  setActiveHallId,
  getActiveHallIdLocalStorage,
} from "common/localStorage";

export type HallsContextType = {
  hallsResponseState: AsyncState<Hall[] | undefined>;
  halls: Hall[];
  selectedHallId: string | undefined;
  selectHall: (hallId: string) => void;
  doFetchHalls: () => Promise<Hall[]>;
  hallsLoaded: boolean;
  includedPlatesOnly: boolean;
};

export const HallsContext = createContext<HallsContextType | undefined>(
  undefined
);

export const HallsContextProvider = ({ children }: ChildrenOnly) => {
  const [selectedHallId, setSelectedHallId] = useState<string | undefined>(
    getActiveHallIdLocalStorage()
  );

  const [halls, setHalls] = useState<Hall[]>([]);
  const [includedPlatesOnly, setIncludedPlatesOnly] = useState<boolean>(false);

  const selectHall = (hallId: string) => {
    setSelectedHallId(hallId);
    setActiveHallId(hallId);
    setIncludedPlatesOnly(
      !!halls.filter(({ id }) => id === hallId)[0]?.includedPlatesOnly
    );
  };

  const [hallsResponseState, { execute: doFetchHalls }] = useAsync(async () => {
    try {
      const response = await getHalls();
      setHalls(response.data);
      if (response.data.length === 1) {
        selectHall(response.data[0].id);
      }
      return response.data;
    } catch (error) {
      const message =
        (error as ApiError)?.response?.data?.error ||
        "Cannot fetch halls. Error occurred.";
      handleError(error, message);
      return [];
    }
  });

  const state: HallsContextType = {
    hallsResponseState,
    halls,
    selectedHallId,
    selectHall,
    doFetchHalls,
    hallsLoaded: halls.length > 0,
    includedPlatesOnly,
  };
  return (
    <HallsContext.Provider value={state}>{children}</HallsContext.Provider>
  );
};

export const useHalls = (): HallsContextType => {
  const context = useContext(HallsContext);
  if (context === undefined)
    throw new Error("useHalls must be used within HallsContextProvider");
  return context;
};
